/**
 * Liveness verification service.
 *
 * Wraps the two endpoints behind the live-selfie step of profile completion:
 *
 *   GET  /user-details/liveness          → has the user already verified?
 *   POST /user-details/liveness          → multipart upload of the captured frame
 */

import axiosInstance from '../config/axios.config';

const BASE = '/user-details/liveness';

const livenessService = {
    /**
     * Check whether the current user has already passed liveness.
     * Used by the wizard step on mount to decide whether to render the
     * "verified" state (and skip the camera) or the capture flow.
     *
     * Also surfaces the saved image URL + lat/lng metadata so the
     * verified state can render the actual selfie + location stamp.
     */
    async getStatus() {
        try {
            const response = await axiosInstance.get(BASE);
            const data = response.data?.data || {};
            return {
                success:    true,
                verified:   !!data.verified,
                verifiedAt: data.verified_at || null,
                imageUrl:   data.image_url || null,
                lat:        data.lat ?? null,
                lng:        data.lng ?? null,
                accuracyM:  data.accuracy_m ?? null,
                capturedAt: data.captured_at || null,
            };
        } catch (error) {
            return {
                success: false,
                verified: false,
                message: error.response?.data?.message || 'Could not load status.',
            };
        }
    },

    /**
     * Upload a captured selfie + geo metadata.
     *
     * @param {object}   payload
     * @param {Blob}     payload.imageBlob       JPEG blob produced by canvas.toBlob
     * @param {?number}  payload.lat
     * @param {?number}  payload.lng
     * @param {?number}  payload.accuracyM
     * @param {?string}  payload.capturedAt      ISO 8601
     */
    async submit({ imageBlob, lat, lng, accuracyM, capturedAt }) {
        const formData = new FormData();
        formData.append('image', imageBlob, 'liveness.jpg');
        if (lat !== null && lat !== undefined)             formData.append('lat', String(lat));
        if (lng !== null && lng !== undefined)             formData.append('lng', String(lng));
        if (accuracyM !== null && accuracyM !== undefined) formData.append('accuracy_m', String(accuracyM));
        if (capturedAt)                                    formData.append('captured_at', capturedAt);

        try {
            const response = await axiosInstance.post(BASE, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return {
                success: !!response.data?.success,
                data:    response.data?.data || null,
                message: response.data?.message,
            };
        } catch (error) {
            const data = error.response?.data;
            return {
                success: false,
                message: data?.message || 'Could not save your verification.',
                errors:  data?.errors || null,
                status:  error.response?.status,
            };
        }
    },
};

export default livenessService;
